import { Transacao } from '../../servicos/transacoes/typeTransacao';

/**
 * Lógica pura da sheet de criação/edição de transação (NovaTransacaoSheet):
 * helpers de data, validação e montagem do payload — extraídos do componente
 * sem mudança de comportamento, para serem testáveis com Jest.
 */

export type Recorrencia = 'unica' | 'fixa' | 'parcelada';
export type ModoEdicao = 'somenteEsta' | 'futuras' | 'todas';

export const paraISO = (data: Date): string => {
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
};

export const deISO = (iso: string): Date => {
  const [ano, mes, dia] = iso.split('-').map(Number);
  return new Date(ano, (mes || 1) - 1, dia || 1);
};

export const rotuloData = (iso: string): string => {
  if (!iso) return 'Escolher data';
  return deISO(iso)
    .toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    .replace('.', '');
};

// Mesmo tratamento de data do formulário antigo: string dd/mm/aaaa → Date local.
export const parseDate = (dateStr: string): Date => {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return new Date();

  const [day, month, year] = parts.map(Number);
  return new Date(year, month - 1, day); // Mês começa do 0 (Janeiro = 0)
};

export const isoParaDMY = (iso: string): string => iso.split('-').reverse().join('/');

/** Estado do formulário da sheet — espelho dos useState do componente. */
export type EstadoTransacaoForm = {
  tipo: 'D' | 'R';
  valor: number;
  descricao: string;
  /** Data no formato ISO do input (aaaa-mm-dd). */
  data: string;
  categoria: string;
  pagamento: string;
  conta: string;
  cartaoCredito: string;
  recorrencia: Recorrencia;
  mesesFixa: string;
  parcelas: string;
  modoEdicao: ModoEdicao;
  edicao: boolean;
  transacao?: Transacao | null;
};

export type ErrosTransacao = {
  valor: string;
  descricao: string;
  data: string;
  meses: string;
  parcelas: string;
};

/** Mapa de erros por campo — string vazia significa campo válido. */
export const validarTransacao = (estado: EstadoTransacaoForm): ErrosTransacao => {
  const mesesNumero = parseInt(estado.mesesFixa);
  const parcelasNumero = parseInt(estado.parcelas);

  return {
    valor: estado.valor > 0 ? '' : 'Informe um valor maior que zero.',
    descricao: estado.descricao.trim() ? '' : 'Dê um nome à movimentação.',
    data: estado.data ? '' : 'Informe a data.',
    meses:
      !estado.edicao && estado.recorrencia === 'fixa' && !(mesesNumero >= 1 && mesesNumero <= 36)
        ? 'Informe de 1 a 36 meses.'
        : '',
    parcelas:
      !estado.edicao && estado.recorrencia === 'parcelada' && !(parcelasNumero >= 1 && parcelasNumero <= 12)
        ? 'Informe de 1 a 12 parcelas.'
        : ''
  };
};

/**
 * Payload idêntico ao que o formulário antigo monta (create e update).
 * Campos de vínculo vazios ficam NaN (parseInt('')) e serializam como null
 * no JSON — contrato atual com o backend, preservado de propósito.
 */
export const montarPayloadTransacao = (estado: EstadoTransacaoForm): Transacao => {
  const { edicao, recorrencia, transacao } = estado;
  const mesesNumero = parseInt(estado.mesesFixa);
  const parcelasNumero = parseInt(estado.parcelas);

  return {
    tipo: estado.tipo,
    valor: estado.valor,
    data: parseDate(isoParaDMY(estado.data)),
    descricao: estado.descricao,
    tipoPagamento: estado.pagamento,
    contaId: parseInt(estado.conta),
    categoriaId: parseInt(estado.categoria),
    cartaoCreditoId: parseInt(estado.cartaoCredito),
    ehFixa: edicao ? transacao?.ehFixa || false : recorrencia === 'fixa',
    totalMeses: !edicao && recorrencia === 'fixa' ? mesesNumero : 0,
    ehParcelada: edicao ? transacao?.ehParcelada || false : recorrencia === 'parcelada',
    parcela: edicao ? transacao?.parcela || 0 : 1,
    totalParcelas: edicao ? transacao?.totalParcelas || 0 : recorrencia === 'parcelada' ? parcelasNumero : 0,
    modoEdicao: edicao && (transacao?.ehFixa || transacao?.ehParcelada) ? estado.modoEdicao : 'somenteEsta'
  };
};
